// infiniteScroll.ts
import type { DirectiveBinding, ObjectDirective } from "vue";

export type InfiniteScrollOptions = {
    callback: () => void | Promise<void>; // REQUIRED
    distance?: number;  // px from bottom to trigger (default 120)
    disabled?: boolean; // disables listener when true
    container?: HTMLElement | Window | string
};

type State = {
    onScroll: () => void;
    container: HTMLElement | Window;
    options: Required<Omit<InfiniteScrollOptions, "callback">> & { callback: InfiniteScrollOptions["callback"] };
};

function isFunction(v: unknown): v is Function {
    return typeof v === "function";
}

function getComputedOverflow(el: Element) {
    const style = getComputedStyle(el as HTMLElement);
    return `${style.overflow}${style.overflowY}${style.overflowX}`;
}

function isScrollable(el: Element) {
    const o = getComputedOverflow(el);
    return /(auto|scroll|overlay)/i.test(o);
}

function resolveContainer(el: HTMLElement, container?: InfiniteScrollOptions["container"]): HTMLElement | Window {
    if (!container) return getScrollContainer(el)

    if (container === window) return window

    if (typeof container === "string") {
        // Try selector globally first
        const found = document.querySelector(container)
        if (found instanceof HTMLElement) return found

        // Optional: try closest() as a fallback
        const closest = el.closest(container)
        if (closest instanceof HTMLElement) return closest

        return window
    }

    // HTMLElement
    return container
}

/**
 * Finds the nearest scrollable container. Falls back to window.
 */
function getScrollContainer(el: HTMLElement): HTMLElement | Window {
    let parent: HTMLElement | null = el;
    while (parent && parent !== document.body) {
        parent = parent.parentElement;
        if (parent && isScrollable(parent)) return parent;
    }
    // Fall back to window (document)
    return window;
}

/**
 * Checks if we’re within distance of the bottom of the scroll area.
 */
function shouldTrigger(el: HTMLElement, container: HTMLElement | Window, distance: number) {
    if (container === window) {
        const rect = el.getBoundingClientRect();
        const viewport = window.innerHeight || document.documentElement.clientHeight;
        return rect.bottom - viewport <= distance;
    } else {
        const c = container as HTMLElement;
        return c.scrollTop + c.clientHeight >= c.scrollHeight - distance;
    }
}

const InfiniteScroll: ObjectDirective<HTMLElement, InfiniteScrollOptions | InfiniteScrollOptions["callback"]> = {
    mounted(el, binding: DirectiveBinding<InfiniteScrollOptions | InfiniteScrollOptions["callback"]>) {
        const opts: InfiniteScrollOptions = isFunction(binding.value)
            ? { callback: binding.value as InfiniteScrollOptions["callback"] }
            : (binding.value as InfiniteScrollOptions) ?? ({} as InfiniteScrollOptions);

        if (!isFunction(opts.callback)) {
            if (import.meta?.env?.DEV) {
                console.warn("[v-infinite-scroll] A callback function is required.");
            }
            return;
        }

        const container = resolveContainer(el, opts.container)
        // Defaults
        const state: State = {
            container,
            options: {
                callback: opts.callback,
                distance: Math.max(0, opts.distance ?? 120),
                disabled: !!opts.disabled,
                container: opts.container
            },
            onScroll: () => { },
        };

        // Throttle with rAF for perf
        let ticking = false;
        let inFlight = false;
        const handle = async () => {
            ticking = false;
            if (state.options.disabled || inFlight) return;
            if (shouldTrigger(el, state.container, state.options.distance)) {
                try {
                    inFlight = true;
                    await state.options.callback();
                } finally {
                    inFlight = false;
                }
            }
        };
        state.onScroll = () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(handle);
        };

        const target = state.container === window ? window : (state.container as HTMLElement);
        target.addEventListener("scroll", state.onScroll, { passive: true });

        // Store on element for later (update/unmounted)
        (el as any).__infiniteScroll__ = state;

        // Initial check in case content is already short
        state.onScroll();
    },

    updated(el, binding) {
        const state: State | undefined = (el as any).__infiniteScroll__
        if (!state) return

        const newOpts: InfiniteScrollOptions = isFunction(binding.value)
            ? { callback: binding.value as InfiniteScrollOptions["callback"] }
            : (binding.value as InfiniteScrollOptions) ?? ({} as InfiniteScrollOptions)

        // merge options
        state.options = {
            callback: (newOpts.callback ?? state.options.callback)!,
            distance: Math.max(0, newOpts.distance ?? state.options.distance ?? 120),
            disabled: !!(newOpts.disabled ?? state.options.disabled),
            container: newOpts.container
        }

        // detect container change
        const nextContainer = resolveContainer(el, newOpts.container)
        if (nextContainer !== state.container) {
            const oldTarget = state.container === window ? window : (state.container as HTMLElement)
            oldTarget.removeEventListener("scroll", state.onScroll as EventListener)

            state.container = nextContainer

            const newTarget = state.container === window ? window : (state.container as HTMLElement)
            if (!state.options.disabled) {
                newTarget.addEventListener("scroll", state.onScroll, { passive: true })
            }
        }

        state.onScroll()
    },

    unmounted(el) {
        const state: State | undefined = (el as any).__infiniteScroll__;
        if (!state) return;
        const target = state.container === window ? window : (state.container as HTMLElement);
        target.removeEventListener("scroll", state.onScroll as EventListener);
        delete (el as any).__infiniteScroll__;
    },
};

export default InfiniteScroll;
